import type { CSSProperties, ReactNode } from 'react';

import { HaiIcon, type IconName } from './icons';

/**
 * CardTitleIcon — 內容卡標題前的圖示(R409;全站單一來源)。
 * -----------------------------------------------------------------------------
 * owner 2026-10-10:「卡片標題前面的 icon 設計不一致,建議以成效報表的設計風格為主。」
 * 改版前同一個角色有四種畫法:成效報表 = 裸 teal 線條圖示 17px;素材清單 / 檢查報告 =
 * D3 `.h-ic` 28px 灰底圓角方塊 + 灰圖示;首頁右欄 = 裸 teal-700 16px。
 * 收斂成報表那一種:**不墊底塊、`--tide-600`、17px**(HaiIcon 線條,stroke 1.6)。
 *
 * ⚠️ 不要在呼叫端再包一層有底色的方塊,也不要另外指定顏色 / 尺寸 —— 要換樣式改這裡,
 * 全站卡頭一起換;各自覆寫就是回到改版前那四種。
 * ⚠️ 顏色用 `--tide-600` 不是 `--concept-fg`:「tide-600 當字對比不足」那條講的是**文字**;
 * 圖示是圖形物件(WCAG 1.4.11 門檻 3:1),報表從 R311 起就是這顆。
 *
 * `name` = HaiIcon 圖示名;HaiIcon 沒有的圖形(例:趨勢折線,D3 icons.js 也沒有)用
 * `children` 傳 svg(寬高用 `CARD_TITLE_ICON_SIZE`),一樣套這層外框。裝飾性 → `aria-hidden`,
 * 語意由旁邊的標題文字提供。
 *
 * **R411 — 範圍擴大到「所有標題前的圖示」**(owner 2026-10-11:「整份專案的 ICON 都要檢查一下。
 * 要風格統一。」):內容卡卡頭之外,對話框 / 抽屜 / 燈箱資訊欄 / 卡內小標題前的圖示也走這支
 * (原本是 26–34px 的灰 / 淡 teal / 紫 / 玫瑰底方塊,五六種)。
 * - `tone`:**只有**刪除類(`danger` → `--rose-700`)與警告類(`warn` → `--amber-700`)對話框
 *   保留語意色 —— 形狀一樣(裸線條、17px),顏色講「這個動作的性質」;其餘一律預設 teal。
 *   兩顆都是會隨主題翻的 700 階前景色(theme.css「語意色」段;`--warn` / `--err` 本身不能當前景)。
 * - `title`:圖示本身帶資訊時(例:檢查報告左欄的素材類型)才給,走全站 `TooltipLayer`。
 * - 不屬於這支的:功能性圖示(排序箭頭 / 勾選 / 播放 / 鎖)、狀態圓點(上傳結果頁成功 / 失敗)、
 *   空狀態的大圖示、入口方塊 —— 角色不同,不要拿這支去套。
 */
export const CARD_TITLE_ICON_SIZE = 17;

export const cardTitleIconStyle: CSSProperties = {
  display: 'flex',
  flexShrink: 0,
  color: 'var(--tide-600)',
};

export type CardTitleIconTone = 'default' | 'danger' | 'warn';

const TONE_COLOR: Record<CardTitleIconTone, string> = {
  default: 'var(--tide-600)',
  danger: 'var(--rose-700)',
  warn: 'var(--amber-700)',
};

type CardTitleIconProps = (
  | { name: IconName; children?: never }
  | { name?: never; children: ReactNode }
) & { testId?: string; tone?: CardTitleIconTone; title?: string };

export function CardTitleIcon({ name, children, testId, tone = 'default', title }: CardTitleIconProps) {
  return (
    <span
      style={tone === 'default' ? cardTitleIconStyle : { ...cardTitleIconStyle, color: TONE_COLOR[tone] }}
      aria-hidden
      title={title}
      data-card-title-icon=""
      data-tone={tone === 'default' ? undefined : tone}
      data-testid={testId}
    >
      {name ? <HaiIcon name={name} size={CARD_TITLE_ICON_SIZE} /> : children}
    </span>
  );
}
